import {ChangeDetectionStrategy, Component, computed, inject, input} from '@angular/core';
import {FlowSlot, stepGroups} from '../../shared/models/export-flow-slots.model';
import {StepAnchorDirective} from '../step-focus/step-anchor.directive';
import {StepFocusService} from '../step-focus/step-focus.service';
import {currentFilledStep, StepStatus, stepStates} from '../step-focus/step-state.model';
import {StepLimitsFabComponent} from '../step-menu/step-limits-fab.component';
import {StepMenuComponent} from '../step-menu/step-menu.component';
import {currentStepId, StepMenuLimit, stepMenuItems} from '../step-menu/step-menu.model';
import {StepBody, StepOutletDirective} from '../step-outlet.directive';

export type FlowStep = {
    id: FlowSlot;
    titleKey: string;
    body: StepBody;
};

@Component({
    selector: 'app-step-menu-flow',
    standalone: true,
    imports: [StepMenuComponent, StepLimitsFabComponent, StepAnchorDirective, StepOutletDirective],
    template: `
        <div class="d-lg-flex align-items-start gap-4">
            <app-step-menu
                [headingKey]="headingKey()"
                [items]="menuItems()"
                [currentId]="currentId()"
                (stepSelected)="stepFocus.select($event)" />

            <div class="step-column flex-grow-1">
                @for (row of rows(); track row.step.id) {
                    <div [appStepAnchor]="row.step.id" [state]="row.state">
                        @for (body of row.bodies; track $index) {
                            <ng-container [appStepOutlet]="body" />
                        }
                    </div>
                }
                <ng-content />
                <app-step-limits-fab
                    [items]="menuItems()"
                    (stepSelected)="stepFocus.select($event)" />
            </div>
        </div>
    `,
    styles: `
        :host {
            display: block;
        }

        .step-column {
            min-width: 0;
            max-width: 55rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [StepFocusService],
})
export class StepMenuFlowComponent {
    protected readonly stepFocus = inject(StepFocusService);

    readonly steps = input.required<ReadonlyArray<FlowStep>>();
    readonly headingKey = input.required<string>();
    readonly limits = input.required<ReadonlyMap<string, StepMenuLimit>>();
    readonly statuses = input.required<ReadonlyMap<string, StepStatus>>();

    private readonly groups = computed(() => stepGroups(this.steps()));

    private readonly leadSteps = computed(() => this.groups().map(([lead]) => lead));

    protected readonly currentId = computed(() =>
        currentStepId(
            this.stepFocus.currentId(),
            this.leadSteps().map(step => step.id),
        ),
    );

    protected readonly states = computed(() =>
        stepStates(
            this.leadSteps().map(step => step.id),
            this.currentId(),
            this.stepFocus.position(),
            this.statuses(),
        ),
    );

    protected readonly rows = computed(() =>
        this.groups().map(group => ({
            step: group[0],
            bodies: group.map(member => member.body),
            state: this.states().get(group[0].id) ?? currentFilledStep,
        })),
    );

    protected readonly menuItems = computed(() =>
        stepMenuItems(this.leadSteps(), this.limits(), this.states()),
    );
}
